/**
 * Lightweight analytics fan-out for GA4 (gtag) + Meta Pixel (fbq).
 *
 * Design rules:
 *  - Safe no-op if either tag is unavailable (script blocked, dev, etc.).
 *  - Never send borrower-specific or financial data (DOB, SSN, income,
 *    credit, loan amount, address, debt, etc.). Only generic, non-sensitive
 *    parameters such as page_path, cta_id, location, calculator_type,
 *    form_type, and broad loan_purpose strings.
 *  - Per-session de-dupe helper for events that should fire once per
 *    session per key (e.g. calculator_use).
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
  }
}

type GenericParams = Record<string, string | number | boolean | undefined>;

/** Allow-list of parameter keys that are always safe to send. */
const SAFE_KEYS = new Set([
  "page_path",
  "page_location",
  "page_title",
  "cta_id",
  "location",
  "calculator_type",
  "form_type",
  "loan_purpose",
  "method",
  "destination",
  "value", // generic engagement value, never a dollar amount
]);

/** Strip anything not on the allow-list before sending. */
const sanitize = (params?: GenericParams): GenericParams => {
  if (!params) return {};
  const out: GenericParams = {};
  for (const [k, v] of Object.entries(params)) {
    if (!SAFE_KEYS.has(k)) continue;
    if (v === undefined || v === null) continue;
    out[k] = v;
  }
  return out;
};

const safeGtag = (...args: any[]) => {
  if (typeof window === "undefined") return;
  try {
    window.gtag?.(...args);
  } catch {
    /* no-op */
  }
};

const safeFbq = (...args: any[]) => {
  if (typeof window === "undefined") return;
  try {
    window.fbq?.(...args);
  } catch {
    /* no-op */
  }
};

/** Fire a GA4 event. */
export const gaEvent = (name: string, params?: GenericParams) => {
  safeGtag("event", name, sanitize(params));
};

/**
 * Fire a Meta Pixel event.
 *  - kind="standard" uses fbq('track', name) for built-in events
 *    (PageView, Lead, Contact, ViewContent, etc.)
 *  - kind="custom"   uses fbq('trackCustom', name) for custom names.
 */
export const metaEvent = (
  name: string,
  kind: "standard" | "custom" = "standard",
  params?: GenericParams,
) => {
  const clean = sanitize(params);
  safeFbq(kind === "standard" ? "track" : "trackCustom", name, clean);
};

/**
 * Fan-out helper: fire a GA4 event and, optionally, a paired Meta event.
 * Pass `meta: null` to skip the Meta side.
 */
export const track = (
  gaName: string,
  meta: { name: string; kind?: "standard" | "custom" } | null = null,
  params?: GenericParams,
) => {
  gaEvent(gaName, params);
  if (meta) metaEvent(meta.name, meta.kind ?? "standard", params);
};

/** Pageview helper: GA4 page_view + Meta PageView. */
export const trackPageview = (path: string, title?: string) => {
  gaEvent("page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : undefined,
    page_title: title,
  });
  metaEvent("PageView", "standard");
};

/* ------------------------------------------------------------------ */
/* Per-session de-dupe (e.g. fire calculator_use once per calculator) */
/* ------------------------------------------------------------------ */

const FIRED_KEY = "__hm_analytics_fired__";

const getFiredSet = (): Set<string> => {
  if (typeof window === "undefined") return new Set();
  const w = window as unknown as { [FIRED_KEY]?: Set<string> };
  if (!w[FIRED_KEY]) w[FIRED_KEY] = new Set<string>();
  return w[FIRED_KEY]!;
};

/** Returns true the first time `key` is seen this session, false after. */
export const onceThisSession = (key: string): boolean => {
  const s = getFiredSet();
  if (s.has(key)) return false;
  s.add(key);
  return true;
};